// src/pages/admin/SubscriptionPlans.jsx - Modified with SEO/OG Fields & Image Upload

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiCalendar,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiDollarSign,
  FiClock,
  FiStar,
  FiCheckCircle,
  FiX,
  FiSave,
  FiTag,
  FiGrid,
  FiList,
  FiEye,
  FiCopy,
  FiTrendingUp,
  FiUsers,
  FiHeart,
  FiLoader,
  FiFilter,
  FiChevronDown,
  FiChevronUp,
  FiMessageCircle,
  FiAward,
  FiZoomIn,
  FiExternalLink,
  FiRepeat,
  FiZap,
  FiShield,
  FiPackage,
  FiAlertCircle,
  FiCheck,
  FiImage,
  FiUpload,
  FiInfo,
  FiShare2,
} from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentUserToken } from '../../services/firebase';

// ============================================
// SOCIAL PREVIEW INDICATOR (UI Only)
// ============================================
const SocialPreviewIndicator = ({ ogImage, ogTitle, ogDescription }) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  if (!ogImage && !ogTitle && !ogDescription) {
    return null;
  }

  return (
    <div
      className="mt-2 p-3 rounded-xl border"
      style={{
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <FiShare2 className="w-3.5 h-3.5 text-blue-500" />
        <span
          className="text-[10px] font-medium"
          style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
        >
          Social Sharing Preview
        </span>
        <span
          className="text-[9px]"
          style={{
            color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
          }}
        >
          (OG tags generated server-side)
        </span>
      </div>
      <div
        className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800"
        style={{ aspectRatio: '1.91/1' }}
      >
        {ogImage ? (
          <>
            <img
              src={ogImage}
              alt="OG Preview"
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
              {ogTitle && (
                <p className="text-white font-semibold text-sm line-clamp-1">
                  {ogTitle}
                </p>
              )}
              {ogDescription && (
                <p className="text-white/70 text-xs line-clamp-1 mt-0.5">
                  {ogDescription}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FiImage className="w-8 h-8 mx-auto opacity-30" />
              <p
                className="text-xs mt-2 opacity-50"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                No OG image set
              </p>
              <p
                className="text-[10px] opacity-30"
                style={{ color: isDark ? '#ffffff' : '#0a0a0a' }}
              >
                Upload a plan image to auto-generate
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function SubscriptionPlans() {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = actualTheme === 'dark';

  // State
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewBillingCycle, setPreviewBillingCycle] = useState('monthly');

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [popularFilter, setPopularFilter] = useState('all');
  const [sortBy, setSortBy] = useState('order');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showFilters, setShowFilters] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    popular: 0,
  });

  // Image state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Form state with all fields including SEO/OG
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    monthlyPrice: '',
    yearlyPrice: '',
    monthlyDuration: 'Month to month',
    yearlyDuration: 'Annual commitment',
    features: [],
    popular: false,
    icon: 'FiCalendar',
    order: 0,
    status: 'active',
    ctaText: 'Get Started',
    highlightText: '',
    savingsBadge: 'Save 15%',
    // SEO & OG Fields
    seoTitle: '',
    seoDescription: '',
    seoKeywords: [],
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
  });

  const [newFeature, setNewFeature] = useState('');

  // API base URL
  const API_BASE = '/api/subscription-plans';

  // Helper to get auth token
  const getAuthToken = async () => {
    return await getCurrentUserToken();
  };

  // Parse comma-separated input
  const parseBulkInput = input => {
    if (!input.trim()) return [];
    let items = input.split(/[,#\n]+/);
    items = items.map(item => item.trim()).filter(item => item.length > 0);
    return [...new Set(items)];
  };

  // ==================== AUTO-GENERATE SEO FIELDS ====================
  const autoGenerateSEOFields = useCallback(() => {
    const title = formData.name || '';
    const description = formData.description || '';
    const tags = formData.features || [];
    const image = imagePreview || formData.ogImage || '';

    const updates = {};

    // SEO Title
    if (!formData.seoTitle && title) {
      updates.seoTitle =
        title.length > 60
          ? title.substring(0, 57) + '... - WD Hejbul'
          : title + ' - WD Hejbul';
    }

    // SEO Description
    if (!formData.seoDescription && description) {
      updates.seoDescription =
        description.length > 160
          ? description.substring(0, 157) + '...'
          : description;
    }

    // OG Title
    if (!formData.ogTitle && title) {
      updates.ogTitle =
        title.length > 90 ? title.substring(0, 87) + '...' : title;
    }

    // OG Description
    if (!formData.ogDescription && description) {
      updates.ogDescription =
        description.length > 200
          ? description.substring(0, 197) + '...'
          : description;
    }

    // OG Image
    if (!formData.ogImage && image) {
      updates.ogImage = image;
    }

    // Twitter fields
    if (!formData.twitterTitle && (updates.ogTitle || formData.ogTitle)) {
      updates.twitterTitle = updates.ogTitle || formData.ogTitle;
    }
    if (
      !formData.twitterDescription &&
      (updates.ogDescription || formData.ogDescription)
    ) {
      updates.twitterDescription =
        updates.ogDescription || formData.ogDescription;
    }
    if (!formData.twitterImage && (updates.ogImage || formData.ogImage)) {
      updates.twitterImage = updates.ogImage || formData.ogImage;
    }

    // SEO Keywords from features
    if (
      (!formData.seoKeywords || formData.seoKeywords.length === 0) &&
      tags.length > 0
    ) {
      updates.seoKeywords = tags.slice(0, 10);
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [formData, imagePreview]);

  // ==================== FEATURE HANDLERS ====================
  const handleFeatureKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (newFeature.trim()) {
        if (newFeature.includes(',') || newFeature.includes('#')) {
          const newItems = parseBulkInput(newFeature);
          if (newItems.length > 0) {
            setFormData(prev => ({
              ...prev,
              features: [...new Set([...prev.features, ...newItems])],
            }));
          }
        } else {
          setFormData(prev => ({
            ...prev,
            features: [...prev.features, newFeature.trim()],
          }));
        }
        setNewFeature('');
      }
    }
  };

  const handleAddFeature = e => {
    if (e) e.preventDefault();
    if (newFeature.trim()) {
      if (newFeature.includes(',') || newFeature.includes('#')) {
        const newItems = parseBulkInput(newFeature);
        if (newItems.length > 0) {
          setFormData(prev => ({
            ...prev,
            features: [...new Set([...prev.features, ...newItems])],
          }));
        }
      } else {
        setFormData(prev => ({
          ...prev,
          features: [...prev.features, newFeature.trim()],
        }));
      }
      setNewFeature('');
    }
  };

  const handleRemoveFeature = index => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // ==================== IMAGE HANDLERS ====================
  const handleImageChange = e => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        const preview = reader.result;
        setImagePreview(preview);
        // Auto-generate OG image from preview
        setFormData(prev => ({ ...prev, ogImage: preview }));
        // Auto-generate all SEO fields
        setTimeout(autoGenerateSEOFields, 100);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, ogImage: '' }));
  };

  // ==================== SELECTION HANDLERS ====================
  const toggleSelect = id => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredPlans.map(p => p.id));
    }
    setSelectAll(!selectAll);
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  // ==================== CASCADE DELETE ====================
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedIds.length} selected plan(s)? This will also delete all associated inquiries and notifications. This action cannot be undone.`,
      )
    )
      return;

    try {
      const token = await getAuthToken();
      let deletedCount = 0;
      let failedCount = 0;
      let totalNotifications = 0;
      let totalInquiries = 0;

      for (const id of selectedIds) {
        try {
          const response = await fetch(`${API_BASE}?action=delete&id=${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await response.json();
          if (data.success) {
            deletedCount++;
            if (data.deletedItems) {
              totalNotifications += data.deletedItems.notifications || 0;
              totalInquiries += data.deletedItems.inquiries || 0;
            }
          } else {
            failedCount++;
          }
        } catch (err) {
          failedCount++;
          console.error(`Failed to delete plan ${id}:`, err);
        }
      }

      alert(
        `Deleted ${deletedCount} plan(s) successfully!\n` +
          `Removed ${totalNotifications} notifications and ${totalInquiries} inquiries.` +
          (failedCount > 0 ? `\n${failedCount} failed.` : ''),
      );
      clearSelection();
      loadPlans();
    } catch (error) {
      console.error('Error in bulk delete:', error);
      alert('Failed to delete selected plans');
    }
  };

  // ==================== LOAD PLANS ====================
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}?action=list`);
      const data = await response.json();

      if (data.success) {
        setPlans(data.plans);
      }

      const statsResponse = await fetch(`${API_BASE}?action=stats`);
      const statsData = await statsResponse.json();
      if (statsData.success) {
        setStats(statsData.stats);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Apply filters and sorting
  const applyFilters = useCallback(() => {
    let filtered = [...plans];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (popularFilter !== 'all') {
      filtered = filtered.filter(
        p => p.popular === (popularFilter === 'popular'),
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.name?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term) ||
          p.features?.some(f => f.toLowerCase().includes(term)),
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'order') {
        aVal = a.order || 999;
        bVal = b.order || 999;
      } else if (sortBy === 'monthlyPrice') {
        aVal = parseFloat(a.monthlyPrice) || 0;
        bVal = parseFloat(b.monthlyPrice) || 0;
      } else if (sortBy === 'name') {
        aVal = a.name || '';
        bVal = b.name || '';
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredPlans(filtered);
  }, [plans, statusFilter, popularFilter, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadPlans();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = await getAuthToken();
      const url = editingPlan
        ? `${API_BASE}?action=update&id=${editingPlan.id}`
        : `${API_BASE}?action=create`;
      const method = editingPlan ? 'PUT' : 'POST';

      // Auto-generate SEO before submit
      autoGenerateSEOFields();

      // Prepare submission data
      const submitData = {
        ...formData,
        // Convert string prices to numbers
        monthlyPrice: parseFloat(formData.monthlyPrice) || 0,
        yearlyPrice: parseFloat(formData.yearlyPrice) || 0,
        // Ensure features is an array
        features: formData.features || [],
        // SEO fields
        seoTitle: formData.seoTitle || '',
        seoDescription: formData.seoDescription || '',
        seoKeywords: formData.seoKeywords || [],
        ogTitle: formData.ogTitle || '',
        ogDescription: formData.ogDescription || '',
        ogImage: formData.ogImage || '',
        twitterTitle: formData.twitterTitle || '',
        twitterDescription: formData.twitterDescription || '',
        twitterImage: formData.twitterImage || '',
      };

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (data.success) {
        alert(
          editingPlan
            ? 'Plan updated successfully!'
            : 'Plan created successfully!',
        );
        resetForm();
        loadPlans();
      } else {
        alert(data.message || 'Failed to save plan');
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      alert('Failed to save plan');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = plan => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      monthlyPrice: plan.monthlyPrice || '',
      yearlyPrice: plan.yearlyPrice || '',
      monthlyDuration: plan.monthlyDuration || 'Month to month',
      yearlyDuration: plan.yearlyDuration || 'Annual commitment',
      features: plan.features || [],
      popular: plan.popular || false,
      icon: plan.icon || 'FiCalendar',
      order: plan.order || 0,
      status: plan.status || 'active',
      ctaText: plan.ctaText || 'Get Started',
      highlightText: plan.highlightText || '',
      savingsBadge: plan.savingsBadge || 'Save 15%',
      // SEO fields
      seoTitle: plan.seoTitle || '',
      seoDescription: plan.seoDescription || '',
      seoKeywords: plan.seoKeywords || [],
      ogTitle: plan.ogTitle || '',
      ogDescription: plan.ogDescription || '',
      ogImage: plan.ogImage || '',
      twitterTitle: plan.twitterTitle || '',
      twitterDescription: plan.twitterDescription || '',
      twitterImage: plan.twitterImage || '',
    });
    setImagePreview(plan.imageUrl || '');
    setImageFile(null);
    setShowModal(true);
  };

  const handleViewDetails = plan => {
    setSelectedPlan(plan);
    setShowDetailModal(true);
  };

  const handleDelete = async id => {
    if (
      !window.confirm(
        'Are you sure you want to delete this plan? This will also delete all associated inquiries and notifications. This action cannot be undone.',
      )
    )
      return;

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE}?action=delete&id=${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        const msg = data.deletedItems
          ? `Plan deleted successfully!\nRemoved ${data.deletedItems.notifications || 0} notifications and ${data.deletedItems.inquiries || 0} inquiries.`
          : 'Plan deleted successfully!';
        alert(msg);
        loadPlans();
        if (showDetailModal) {
          setShowDetailModal(false);
          setSelectedPlan(null);
        }
        setSelectedIds(prev => prev.filter(sid => sid !== id));
      } else {
        alert(data.message || 'Failed to delete plan');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      alert('Failed to delete plan');
    }
  };

  const handleDuplicate = async plan => {
    try {
      const token = await getAuthToken();
      const duplicateData = {
        ...plan,
        name: `${plan.name} (Copy)`,
        order: plans.length,
        status: 'draft',
        popular: false,
      };
      delete duplicateData.id;
      delete duplicateData.createdAt;
      delete duplicateData.updatedAt;

      const response = await fetch(`${API_BASE}?action=create`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(duplicateData),
      });

      const data = await response.json();
      if (data.success) {
        alert('Plan duplicated successfully!');
        loadPlans();
      } else {
        alert(data.message || 'Failed to duplicate plan');
      }
    } catch (error) {
      console.error('Error duplicating plan:', error);
      alert('Failed to duplicate plan');
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      monthlyPrice: '',
      yearlyPrice: '',
      monthlyDuration: 'Month to month',
      yearlyDuration: 'Annual commitment',
      features: [],
      popular: false,
      icon: 'FiCalendar',
      order: 0,
      status: 'active',
      ctaText: 'Get Started',
      highlightText: '',
      savingsBadge: 'Save 15%',
      seoTitle: '',
      seoDescription: '',
      seoKeywords: [],
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
    });
    setImageFile(null);
    setImagePreview('');
    setNewFeature('');
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setPopularFilter('all');
    setSearchTerm('');
    setSortBy('order');
    setSortOrder('asc');
  };

  const calculateYearlySavings = (monthlyPrice, yearlyPrice) => {
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - yearlyPrice;
    const savingsPercent = Math.round((savings / monthlyTotal) * 100);
    return savingsPercent > 0 ? savingsPercent : 0;
  };

  const iconOptions = [
    'FiCalendar',
    'FiZap',
    'FiStar',
    'FiHeart',
    'FiTrendingUp',
    'FiUsers',
    'FiAward',
    'FiShield',
    'FiPackage',
    'FiGrid',
  ];

  const getIconComponent = iconName => {
    const icons = {
      FiCalendar,
      FiZap,
      FiStar,
      FiHeart,
      FiTrendingUp,
      FiUsers,
      FiAward,
      FiShield,
      FiPackage,
      FiGrid,
    };
    return icons[iconName] || FiCalendar;
  };

  // ==================== STATUS BADGE ====================
  const getStatusBadge = status => {
    if (status === 'active') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800/30">
          ● Active
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
        ○ Draft
      </span>
    );
  };

  // ==================== PLAN CARD ====================
  const PlanCard = ({ plan }) => {
    const [localPreviewCycle, setLocalPreviewCycle] = useState('monthly');
    const savingsPercent = calculateYearlySavings(
      plan.monthlyPrice,
      plan.yearlyPrice,
    );
    const isSelected = selectedIds.includes(plan.id);

    return (
      <div
        className={`group relative rounded-2xl overflow-hidden border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
          isSelected ? 'ring-2 ring-black dark:ring-white' : ''
        }`}
        style={{
          backgroundColor: 'var(--bg)',
          borderColor: 'var(--border)',
        }}
      >
        {/* Selection Checkbox - Top Left */}
        <div className="absolute top-3 left-3 z-10">
          <button
            onClick={e => {
              e.stopPropagation();
              toggleSelect(plan.id);
            }}
            className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all hover:scale-110"
            style={{
              borderColor: isSelected ? 'var(--text)' : 'var(--border)',
              backgroundColor: isSelected ? 'var(--text)' : 'transparent',
            }}
          >
            {isSelected && (
              <FiCheck className="w-3 h-3" style={{ color: 'var(--bg)' }} />
            )}
          </button>
        </div>

        {/* Badges - Top Right */}
        <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1.5">
          {plan.popular && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 bg-black text-white dark:bg-white dark:text-black shadow-sm">
              <FiStar className="w-3 h-3" />
              Popular
            </span>
          )}
          {plan.popular && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30">
              Best Value
            </span>
          )}
          {getStatusBadge(plan.status)}
        </div>

        <div className="p-5">
          {/* Icon & Image Section */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--border)' }}
            >
              {plan.imageUrl ? (
                <img
                  src={plan.imageUrl}
                  alt={plan.name}
                  className="w-full h-full object-cover rounded-xl"
                />
              ) : (
                React.createElement(getIconComponent(plan.icon), {
                  className: 'w-6 h-6',
                  style: { color: 'var(--text)' },
                })
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3
                className="text-lg font-bold truncate"
                style={{ color: 'var(--text)' }}
              >
                {plan.name}
              </h3>
              <p
                className="text-sm line-clamp-1"
                style={{ color: 'var(--muted)' }}
              >
                {plan.description}
              </p>
            </div>
          </div>

          {/* Billing Cycle Preview Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setLocalPreviewCycle('monthly')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                localPreviewCycle === 'monthly'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'border'
              }`}
              style={{ borderColor: 'var(--border)' }}
            >
              Monthly
            </button>
            <button
              onClick={() => setLocalPreviewCycle('yearly')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                localPreviewCycle === 'yearly'
                  ? 'bg-black text-white dark:bg-white dark:text-black'
                  : 'border'
              }`}
              style={{ borderColor: 'var(--border)' }}
            >
              Yearly{' '}
              {plan.savingsBadge && (
                <span className="text-green-500 ml-1">{plan.savingsBadge}</span>
              )}
            </button>
          </div>

          {/* Price Display */}
          <div className="mb-4">
            <div className="flex items-baseline gap-1">
              <span
                className="text-3xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                $
                {localPreviewCycle === 'monthly'
                  ? plan.monthlyPrice
                  : plan.yearlyPrice}
              </span>
              <span className="text-sm" style={{ color: 'var(--muted)' }}>
                /{localPreviewCycle === 'monthly' ? 'mo' : 'yr'}
              </span>
            </div>
            {localPreviewCycle === 'yearly' && savingsPercent > 0 && (
              <div className="text-xs text-green-500 mt-1">
                Save {savingsPercent}% vs monthly
              </div>
            )}
            <div className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
              {localPreviewCycle === 'monthly'
                ? plan.monthlyDuration
                : plan.yearlyDuration}
            </div>
          </div>

          {/* Features Preview */}
          <div className="flex flex-wrap gap-1 mb-4">
            {plan.features?.slice(0, 3).map((feature, idx) => (
              <span
                key={idx}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                {feature.length > 20
                  ? feature.substring(0, 20) + '...'
                  : feature}
              </span>
            ))}
            {plan.features?.length > 3 && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                +{plan.features.length - 3}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleEdit(plan)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiEdit2 className="w-3 h-3" />
              Edit
            </button>
            <button
              onClick={() => handleViewDetails(plan)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
              }}
            >
              <FiEye className="w-3 h-3" />
              Details
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ==================== DETAIL MODAL ====================
  const DetailModal = () => {
    if (!selectedPlan) return null;
    const savingsPercent = calculateYearlySavings(
      selectedPlan.monthlyPrice,
      selectedPlan.yearlyPrice,
    );

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={() => setShowDetailModal(false)}
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowDetailModal(false)}
        />

        <div
          className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl animate-fade-in-up"
          style={{ backgroundColor: 'var(--bg)' }}
          onClick={e => e.stopPropagation()}
        >
          <div
            className="sticky top-0 z-10 p-6 border-b"
            style={{
              backgroundColor: 'var(--bg)',
              borderColor: 'var(--border)',
            }}
          >
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {selectedPlan.popular && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-black text-white dark:bg-white dark:text-black flex items-center gap-1">
                      <FiStar className="w-3 h-3" /> Most Popular
                    </span>
                  )}
                  {getStatusBadge(selectedPlan.status)}
                </div>
                <h2
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {selectedPlan.name}
                </h2>
              </div>
              <div className="flex gap-2">
                <a
                  href="/subscription"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <FiExternalLink
                    className="w-5 h-5"
                    style={{ color: 'var(--text)' }}
                  />
                </a>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Image if exists */}
            {selectedPlan.imageUrl && (
              <div className="rounded-xl overflow-hidden">
                <img
                  src={selectedPlan.imageUrl}
                  alt={selectedPlan.name}
                  className="w-full h-48 object-cover"
                />
              </div>
            )}

            <div>
              <h3
                className="text-lg font-semibold mb-2 flex items-center gap-2"
                style={{ color: 'var(--text)' }}
              >
                <FiMessageCircle className="w-4 h-4" />
                Description
              </h3>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                {selectedPlan.description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                  Monthly Plan
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  ${selectedPlan.monthlyPrice}
                  <span className="text-sm font-normal">/mo</span>
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                  {selectedPlan.monthlyDuration}
                </p>
              </div>
              <div
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <p className="text-xs mb-1" style={{ color: 'var(--muted)' }}>
                  Yearly Plan
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  ${selectedPlan.yearlyPrice}
                  <span className="text-sm font-normal">/yr</span>
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                  {selectedPlan.yearlyDuration}
                </p>
                {savingsPercent > 0 && (
                  <p className="text-xs text-green-500 mt-1">
                    Save {savingsPercent}% annually
                  </p>
                )}
              </div>
            </div>

            {selectedPlan.features && selectedPlan.features.length > 0 && (
              <div>
                <h3
                  className="text-lg font-semibold mb-3 flex items-center gap-2"
                  style={{ color: 'var(--text)' }}
                >
                  <FiCheckCircle className="w-4 h-4 text-green-500" />
                  Features Included
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {selectedPlan.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-sm"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div
              className="pt-4 border-t flex justify-between text-xs"
              style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}
            >
              <span>
                Created: {new Date(selectedPlan.createdAt).toLocaleDateString()}
              </span>
              <span>Order: {selectedPlan.order || 0}</span>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  handleEdit(selectedPlan);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                <FiEdit2 className="w-4 h-4" />
                Edit Plan
              </button>
              <button
                onClick={() => handleDuplicate(selectedPlan)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiCopy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={() => handleDelete(selectedPlan.id)}
                className="px-4 py-2 rounded-xl transition-all hover:opacity-80"
                style={{ backgroundColor: '#ef4444', color: 'white' }}
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Subscription Plans
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>
            Manage monthly and yearly subscription plans with dynamic pricing
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 bg-red-500 text-white"
            >
              <FiTrash2 className="w-4 h-4" />
              <span className="text-sm">
                Delete Selected ({selectedIds.length})
              </span>
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <FiPlus className="w-4 h-4" />
            <span className="text-sm">Add Plan</span>
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            <FiRefreshCw
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
            />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setStatusFilter('all');
          }}
        >
          <div className="text-2xl font-bold text-purple-500">
            {stats.total}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Total Plans
          </div>
        </div>
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setStatusFilter('active');
          }}
        >
          <div className="text-2xl font-bold text-green-500">
            {stats.active}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Active Plans
          </div>
        </div>
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setPopularFilter('popular');
          }}
        >
          <div className="text-2xl font-bold text-yellow-500">
            {stats.popular}
          </div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Popular Plans
          </div>
        </div>
        <div
          className="p-4 rounded-xl border cursor-pointer hover:scale-105 transition-all"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
          onClick={() => {
            resetFilters();
            setStatusFilter('draft');
          }}
        >
          <div className="text-2xl font-bold text-gray-500">{stats.draft}</div>
          <div className="text-xs" style={{ color: 'var(--muted)' }}>
            Draft Plans
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
            style={{ color: 'var(--muted)' }}
          />
          <input
            type="text"
            placeholder="Search plans..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl outline-none transition-all"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${showFilters ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ color: 'var(--text)' }}
          >
            <FiFilter className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Filters</span>
            <FiChevronDown
              className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ color: 'var(--text)' }}
          >
            <FiGrid className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-black/10 dark:bg-white/10' : ''}`}
            style={{ color: 'var(--text)' }}
          >
            <FiList className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div
          className="p-4 rounded-xl border animate-fade-in"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Status
              </label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
              </select>
            </div>
            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Popularity
              </label>
              <select
                value={popularFilter}
                onChange={e => setPopularFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
              >
                <option value="all">All</option>
                <option value="popular">Popular Only</option>
                <option value="normal">Normal Only</option>
              </select>
            </div>
            <div>
              <label
                className="text-xs font-medium mb-1 block"
                style={{ color: 'var(--muted)' }}
              >
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="order">Order</option>
                  <option value="name">Name</option>
                  <option value="monthlyPrice">Monthly Price</option>
                </select>
                <button
                  onClick={() =>
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
                  }
                  className="px-3 py-2 rounded-lg transition-all"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  {sortOrder === 'asc' ? (
                    <FiChevronUp className="w-4 h-4" />
                  ) : (
                    <FiChevronDown className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center mt-4">
            <div className="text-xs" style={{ color: 'var(--muted)' }}>
              {selectedIds.length > 0 && `${selectedIds.length} selected`}
            </div>
            <div className="flex gap-2">
              {selectedIds.length > 0 && (
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded-lg text-xs transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  Clear Selection
                </button>
              )}
              <button
                onClick={resetFilters}
                className="px-4 py-1.5 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plans Grid/List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div
            className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--text)' }}
          />
        </div>
      ) : filteredPlans.length === 0 ? (
        <div
          className="text-center py-12 rounded-2xl border"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <FiCalendar
            className="w-12 h-12 mx-auto mb-3 opacity-50"
            style={{ color: 'var(--muted)' }}
          />
          <p style={{ color: 'var(--muted)' }}>No subscription plans found</p>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            Create your first plan
          </button>
        </div>
      ) : (
        <>
          {/* Select All */}
          <div className="flex items-center gap-3 px-1">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-2 text-sm transition-all hover:opacity-80"
              style={{ color: 'var(--muted)' }}
            >
              <span
                className="w-4 h-4 rounded border-2 flex items-center justify-center"
                style={{ borderColor: 'var(--border)' }}
              >
                {selectAll && (
                  <FiCheck
                    className="w-3 h-3"
                    style={{ color: 'var(--text)' }}
                  />
                )}
              </span>
              {selectAll ? 'Deselect All' : 'Select All'}
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                ({filteredPlans.length} items)
              </span>
            </button>
            {selectedIds.length > 0 && (
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                • {selectedIds.length} selected
              </span>
            )}
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPlans.map(plan => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPlans.map(plan => (
                <div
                  key={plan.id}
                  className={`p-4 rounded-xl border transition-all hover:shadow-lg ${
                    selectedIds.includes(plan.id)
                      ? 'ring-2 ring-black dark:ring-white'
                      : ''
                  }`}
                  style={{
                    backgroundColor: 'var(--bg)',
                    borderColor: 'var(--border)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelect(plan.id)}
                      className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all hover:scale-110 flex-shrink-0"
                      style={{
                        borderColor: selectedIds.includes(plan.id)
                          ? 'var(--text)'
                          : 'var(--border)',
                        backgroundColor: selectedIds.includes(plan.id)
                          ? 'var(--text)'
                          : 'transparent',
                      }}
                    >
                      {selectedIds.includes(plan.id) && (
                        <FiCheck
                          className="w-3 h-3"
                          style={{ color: 'var(--bg)' }}
                        />
                      )}
                    </button>

                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      {plan.imageUrl ? (
                        <img
                          src={plan.imageUrl}
                          alt={plan.name}
                          className="w-full h-full object-cover rounded-xl"
                        />
                      ) : (
                        React.createElement(getIconComponent(plan.icon), {
                          className: 'w-6 h-6',
                          style: { color: 'var(--text)' },
                        })
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3
                          className="font-semibold"
                          style={{ color: 'var(--text)' }}
                        >
                          {plan.name}
                        </h3>
                        {plan.popular && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-black text-white dark:bg-white dark:text-black flex items-center gap-1">
                            <FiStar className="w-2 h-2" /> Popular
                          </span>
                        )}
                        {getStatusBadge(plan.status)}
                      </div>
                      <p
                        className="text-sm line-clamp-1"
                        style={{ color: 'var(--muted)' }}
                      >
                        {plan.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span
                          className="text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          ${plan.monthlyPrice}/mo
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          ${plan.yearlyPrice}/yr
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(plan)}
                        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <FiEdit2
                          className="w-4 h-4"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                      <button
                        onClick={() => handleViewDetails(plan)}
                        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <FiEye
                          className="w-4 h-4"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                      <button
                        onClick={() => handleDuplicate(plan)}
                        className="p-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5"
                      >
                        <FiCopy
                          className="w-4 h-4"
                          style={{ color: 'var(--muted)' }}
                        />
                      </button>
                      <button
                        onClick={() => handleDelete(plan.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10"
                      >
                        <FiTrash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal with SEO/OG Fields */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl animate-fade-in-up"
            style={{ backgroundColor: 'var(--bg)' }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="sticky top-0 z-10 flex justify-between items-center p-5 border-b"
              style={{
                backgroundColor: 'var(--bg)',
                borderColor: 'var(--border)',
              }}
            >
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
              >
                <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label
                  className="text-sm font-medium mb-1 block"
                  style={{ color: 'var(--text)' }}
                >
                  Plan Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, name: e.target.value }))
                  }
                  required
                  className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="e.g., Professional"
                />
              </div>
              <div>
                <label
                  className="text-sm font-medium mb-1 block"
                  style={{ color: 'var(--text)' }}
                >
                  Description *
                </label>
                <textarea
                  rows="3"
                  value={formData.description}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  required
                  className="w-full px-4 py-2 rounded-xl outline-none transition-all resize-none"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                  placeholder="Describe this plan..."
                />
              </div>

              {/* Image Upload */}
              <div>
                <label
                  className="text-sm font-medium mb-1 block"
                  style={{ color: 'var(--text)' }}
                >
                  Plan Image (Optional)
                </label>
                <div className="flex items-center gap-4">
                  <div
                    className="w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Plan preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FiImage
                        className="w-8 h-8"
                        style={{ color: 'var(--muted)' }}
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <span
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition-all hover:opacity-80"
                        style={{
                          backgroundColor: 'var(--border)',
                          color: 'var(--text)',
                        }}
                      >
                        <FiUpload className="w-4 h-4" />
                        {imagePreview ? 'Change Image' : 'Upload Image'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                    {imagePreview && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="ml-2 text-sm text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                    <p
                      className="text-xs mt-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      Recommended: 1200x630px for social sharing
                    </p>
                  </div>
                </div>
              </div>

              {/* Monthly and Yearly Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Monthly Price ($) *
                  </label>
                  <input
                    type="number"
                    value={formData.monthlyPrice}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        monthlyPrice: e.target.value,
                      }))
                    }
                    required
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., 2499"
                  />
                </div>
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Yearly Price ($) *
                  </label>
                  <input
                    type="number"
                    value={formData.yearlyPrice}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        yearlyPrice: e.target.value,
                      }))
                    }
                    required
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., 24990"
                  />
                </div>
              </div>

              {/* Monthly and Yearly Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Monthly Duration
                  </label>
                  <input
                    type="text"
                    value={formData.monthlyDuration}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        monthlyDuration: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Month to month"
                  />
                </div>
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Yearly Duration
                  </label>
                  <input
                    type="text"
                    value={formData.yearlyDuration}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        yearlyDuration: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Annual commitment"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        order: parseInt(e.target.value) || 0,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Savings Badge
                  </label>
                  <input
                    type="text"
                    value={formData.savingsBadge}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        savingsBadge: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="e.g., Save 15%"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    CTA Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.ctaText}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        ctaText: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Get Started"
                  />
                </div>
                <div>
                  <label
                    className="text-sm font-medium mb-1 block"
                    style={{ color: 'var(--text)' }}
                  >
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, icon: e.target.value }))
                    }
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    {iconOptions.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.popular}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        popular: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm" style={{ color: 'var(--text)' }}>
                    Mark as Popular
                  </span>
                </label>
                <select
                  value={formData.status}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, status: e.target.value }))
                  }
                  className="px-3 py-1.5 rounded-xl text-sm outline-none"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                </select>
              </div>

              {/* Features */}
              <div
                className="border-t pt-4"
                style={{ borderColor: 'var(--border)' }}
              >
                <label
                  className="text-sm font-medium mb-2 block"
                  style={{ color: 'var(--text)' }}
                >
                  Features *
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={e => setNewFeature(e.target.value)}
                    onKeyDown={handleFeatureKeyDown}
                    placeholder="Add a feature (or paste comma-separated list)..."
                    className="flex-1 px-4 py-2 rounded-xl outline-none transition-all"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddFeature}
                    className="px-4 py-2 rounded-xl transition-all hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => handleRemoveFeature(idx)}
                        className="ml-1 hover:text-red-500"
                      >
                        <FiX className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>
                  💡 Tip: You can paste multiple items separated by commas
                  (e.g., "Feature 1, Feature 2, Feature 3"). Press Enter to add.
                </p>
              </div>

              {/* ============================================ */}
              {/* SEO & OG SECTION */}
              {/* ============================================ */}
              <div
                className="border-t pt-4 space-y-4"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center gap-2">
                  <FiTrendingUp className="w-4 h-4 text-blue-500" />
                  <h3
                    className="text-sm font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    SEO & Social Sharing
                  </h3>
                  <button
                    type="button"
                    onClick={autoGenerateSEOFields}
                    className="px-2 py-0.5 text-[10px] rounded transition-all hover:opacity-80"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    <FiCopy className="inline w-3 h-3 mr-1" />
                    Auto-generate
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* SEO Title */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      SEO Title
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (50-60 chars)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          seoTitle: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="SEO Title"
                      maxLength="60"
                    />
                  </div>

                  {/* OG Title */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      OG Title (Facebook/LinkedIn)
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (60-90 chars)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.ogTitle}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          ogTitle: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="OG Title"
                      maxLength="95"
                    />
                  </div>

                  {/* SEO Description */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      SEO Description
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (150-160 chars)
                      </span>
                    </label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          seoDescription: e.target.value,
                        }))
                      }
                      rows="2"
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="SEO Description"
                      maxLength="160"
                    />
                  </div>

                  {/* OG Description */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      OG Description
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (150-200 chars)
                      </span>
                    </label>
                    <textarea
                      value={formData.ogDescription}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          ogDescription: e.target.value,
                        }))
                      }
                      rows="2"
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all resize-none"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="OG Description"
                      maxLength="200"
                    />
                  </div>

                  {/* OG Image */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      OG Image URL
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (Auto-filled from image)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.ogImage}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          ogImage: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="https://res.cloudinary.com/..."
                    />
                  </div>

                  {/* Twitter Image */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      Twitter Image URL
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (Auto-filled from OG)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.twitterImage}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          twitterImage: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="https://res.cloudinary.com/..."
                    />
                  </div>

                  {/* SEO Keywords */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      SEO Keywords
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (5-10 keywords)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.seoKeywords.join(', ')}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          seoKeywords: e.target.value
                            .split(',')
                            .map(k => k.trim())
                            .filter(k => k),
                        }))
                      }
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="keyword1, keyword2, keyword3"
                    />
                  </div>

                  {/* Twitter Title */}
                  <div>
                    <label
                      className="block text-[10px] font-medium mb-1"
                      style={{ color: 'var(--text)' }}
                    >
                      Twitter Title
                      <span
                        className="ml-1 text-[9px]"
                        style={{ color: 'var(--muted)' }}
                      >
                        (Auto-filled from OG)
                      </span>
                    </label>
                    <input
                      type="text"
                      value={formData.twitterTitle}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          twitterTitle: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-1.5 rounded-lg text-sm outline-none transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="Twitter Title"
                    />
                  </div>
                </div>

                {/* Social Preview Indicator */}
                <SocialPreviewIndicator
                  ogImage={formData.ogImage}
                  ogTitle={formData.ogTitle}
                  ogDescription={formData.ogDescription}
                />

                <div
                  className="p-2 rounded-lg text-[10px]"
                  style={{
                    backgroundColor: isDark
                      ? 'rgba(59, 130, 246, 0.08)'
                      : 'rgba(59, 130, 246, 0.04)',
                    border: '1px solid rgba(59, 130, 246, 0.1)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <FiInfo className="w-3 h-3 text-blue-500 flex-shrink-0" />
                    <span
                      style={{
                        color: isDark
                          ? 'rgba(255,255,255,0.6)'
                          : 'rgba(0,0,0,0.5)',
                      }}
                    >
                      These fields are stored in the database and used by the
                      server to generate OG tags when your plan is shared on
                      social media.
                    </span>
                  </div>
                </div>
              </div>

              <div
                className="flex gap-3 pt-4 border-t"
                style={{ borderColor: 'var(--border)' }}
              >
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  {submitting ? (
                    <FiLoader className="w-4 h-4 animate-spin" />
                  ) : (
                    <FiSave className="w-4 h-4" />
                  )}
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2.5 rounded-xl transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailModal && <DetailModal />}
    </div>
  );
}

export default SubscriptionPlans;
